import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

interface FormData {
  name: string;
  email: string;
  phone: string;
  message: string;
  consent: boolean;
}

interface WidgetSettings {
  school_name?: string;
  primary_color?: string;
  secondary_color?: string;
  accent_color?: string;
  logo_url?: string;
}

const EnrollForm: React.FC = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [fetchingSettings, setFetchingSettings] = useState(true);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [settings, setSettings] = useState<WidgetSettings>({
    school_name: 'My Academy',
    primary_color: '#4F46E5',
    secondary_color: '#7C3AED',
    accent_color: '#10B981'
  });

  const [formData, setFormData] = useState<FormData>({
    name: '',
    email: '',
    phone: '',
    message: '',
    consent: false
  });

  // Fetch widget settings on mount
  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';
        const response = await fetch(`${API_URL}/widget/config`);
        if (response.ok) {
          const data = await response.json();
          setSettings({
            school_name: data.school_name || 'My Academy',
            primary_color: data.primary_color || '#4F46E5',
            secondary_color: data.secondary_color || '#7C3AED',
            accent_color: data.accent_color || '#10B981',
            logo_url: data.logo_url
          });
        }
      } catch (error) {
        console.error('Failed to fetch widget settings:', error);
        // Keep default settings if fetch fails
      } finally {
        setFetchingSettings(false);
      }
    };
    fetchSettings();
  }, []);

  // Load Google Analytics only for enrollment form
  useEffect(() => {
    // Inject GA script
    const script1 = document.createElement('script');
    script1.src = 'https://www.googletagmanager.com/gtag/js?id=G-42R5PVVB4Z';
    script1.async = true;
    document.head.appendChild(script1);

    const script2 = document.createElement('script');
    script2.innerHTML = `
      window.dataLayer = window.dataLayer || [];
      function gtag(){dataLayer.push(arguments);}
      gtag('js', new Date());
      gtag('config', 'G-42R5PVVB4Z');
    `;
    document.head.appendChild(script2);

    // Cleanup function to remove scripts when component unmounts
    return () => {
      document.head.removeChild(script1);
      document.head.removeChild(script2);
    };
  }, []);

  // Validation helpers
  const validateEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email.trim());
  };

  const validatePhone = (phone: string): boolean => {
    if (!phone || !phone.trim()) return true; // Phone is optional
    const phoneRegex = /^[\d\s\-()]+$/;
    return phoneRegex.test(phone.trim()) && phone.replace(/\D/g, '').length >= 10;
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    // Email validation
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!validateEmail(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    // Phone validation
    if (!formData.phone.trim()) {
      newErrors.phone = 'Phone number is required';
    } else if (!validatePhone(formData.phone)) {
      newErrors.phone = 'Please enter a valid phone number (at least 10 digits)';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      // Scroll to first error
      const firstError = document.querySelector('.error-message');
      if (firstError) {
        firstError.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
      return;
    }

    setLoading(true);

    try {
      const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

      // Simple contact form submission
      const submitData = {
        name: formData.name.trim() || null,
        email: formData.email.trim(),
        phone: formData.phone.trim(),
        message: formData.message.trim() || null,
        newsletter: formData.consent
      };

      console.log('Submitting contact form:', submitData);

      const response = await fetch(`${API_URL}/api/public/contact`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(submitData)
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.error || 'Failed to submit form');
      }

      console.log('Form submitted successfully:', result);

      // Navigate to success page with type parameter for GA4 tracking
      navigate('/contact/success?type=contact', {
        state: {
          email: formData.email,
          message: 'Thank you! We\'ll be in touch soon.'
        }
      });

    } catch (error: any) {
      console.error('Form submission error:', error);
      alert('There was an error submitting your form. Please try again or contact us directly.');
    } finally {
      setLoading(false);
    }
  };

  // Show loading spinner while fetching settings
  if (fetchingSettings) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-white to-purple-50 flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-16 w-16 border-b-4 border-t-4" style={{ borderColor: settings.primary_color }}></div>
          <p className="mt-4 text-lg text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-white to-purple-50 py-8 px-4 sm:px-6 lg:px-8">
      <div className="max-w-3xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          {/* Logo */}
          {settings.logo_url && (
            <div className="mb-6 flex justify-center">
              <img
                src={settings.logo_url}
                alt={`${settings.school_name} Logo`}
                className="h-24 w-24 object-contain"
              />
            </div>
          )}

          <h1 className="text-4xl font-bold text-gray-900 mb-2">
            Contact {settings.school_name}
          </h1>
          <p className="text-lg text-gray-600">
            Get in touch with us - we'd love to hear from you
          </p>
        </div>

        {/* Form Card */}
        <form onSubmit={handleSubmit} className="bg-white rounded-2xl shadow-xl p-6 sm:p-8 space-y-6">

          <div className="space-y-4">
            {/* Name (Optional) */}
            <div>
              <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
                Name (optional)
              </label>
              <input
                type="text"
                id="name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition"
                placeholder="Your name"
              />
            </div>

            {/* Email */}
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                Email Address <span className="text-red-500">*</span>
              </label>
              <input
                type="email"
                id="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                className={`w-full px-4 py-3 border ${errors.email ? 'border-red-500' : 'border-gray-300'} rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition`}
                placeholder="your.email@example.com"
              />
              {errors.email && (
                <p className="error-message mt-1 text-sm text-red-600">{errors.email}</p>
              )}
            </div>

            {/* Phone */}
            <div>
              <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-1">
                Phone <span className="text-red-500">*</span>
              </label>
              <input
                type="tel"
                id="phone"
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                className={`w-full px-4 py-3 border ${errors.phone ? 'border-red-500' : 'border-gray-300'} rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition`}
                placeholder="(555) 123-4567"
              />
              {errors.phone && (
                <p className="error-message mt-1 text-sm text-red-600">{errors.phone}</p>
              )}
            </div>

            {/* Message (Optional) */}
            <div>
              <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-1">
                Message (optional)
              </label>
              <textarea
                id="message"
                rows={4}
                value={formData.message}
                onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition resize-none"
                placeholder="Tell us how we can help..."
              />
            </div>

            </div>

          {/* Consent Notice */}
          <label className="flex items-start gap-2 text-sm text-gray-600">
            <input
              type="checkbox"
              checked={formData.consent}
              onChange={(e) => setFormData({ ...formData, consent: e.target.checked })}
              className="mt-1 h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
            />
            By submitting this form, you agree to receive email communications from Armenta Learning Academy about our programs and enrollment opportunities.
          </label>

          {/* Submit Button */}
          <div className="pt-4">
            <button
              type="submit"
              disabled={loading}
              className="w-full py-4 px-6 rounded-lg font-semibold text-lg transition shadow-lg text-white"
              style={{
                backgroundColor: loading ? '#9CA3AF' : settings.primary_color,
                cursor: loading ? 'not-allowed' : 'pointer'
              }}
              onMouseEnter={(e) => {
                if (!loading && settings.secondary_color) {
                  e.currentTarget.style.backgroundColor = settings.secondary_color;
                }
              }}
              onMouseLeave={(e) => {
                if (!loading && settings.primary_color) {
                  e.currentTarget.style.backgroundColor = settings.primary_color;
                }
              }}
            >
              {loading ? (
                <span className="flex items-center justify-center">
                  <svg className="animate-spin h-5 w-5 mr-3" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                  </svg>
                  Sending...
                </span>
              ) : (
                'Send Message'
              )}
            </button>

            {/* Privacy Notice */}
            <p className="text-center text-sm text-gray-500 mt-4">
              🔒 Your information is secure and will never be shared. We'll only use it to send you relevant resources and information.
            </p>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EnrollForm;
